import { Minus, Plus, Trash2, TriangleAlert } from "lucide-react";
import { useEffect, useMemo, useReducer, useRef, useState } from "react";

import { Button, Money, StatusPill } from "@/components/ui";
import { ApiError } from "@/lib/api";
import {
  canCharge,
  cartReducer,
  emptyCart,
  MAX_DISCOUNT,
  type CartLine,
} from "@/lib/cart";
import { resolvePrice, searchProducts, type SalePublic, type SearchHit } from "@/lib/endpoints";
import { newIdempotencyKey } from "@/lib/http";
import { POS_SHORTCUTS, useShortcuts } from "@/lib/keyboard";
import { searchCatalog, type CachedProduct } from "@/lib/offline/db";
import { useCatalogSync, useOnlineStatus, useOutbox } from "@/lib/offline/hooks";
import { submitSale } from "@/lib/offline/outbox";
import { getSession } from "@/lib/session";
import { cartTotals, lineTotals } from "@/lib/totals";

import { PaymentDialog } from "./PaymentDialog";
import { ReceiptDialog } from "./ReceiptDialog";

interface Receipt {
  folio: string;
  sale: SalePublic | null;
}

/**
 * Pantalla de venta.
 *
 * Tres decisiones que la hacen usable en un mostrador real:
 *
 * 1. **El buscador tiene el foco siempre.** El lector de barras escribe y pulsa
 *    Enter: si el foco está en otro sitio, el escaneo se pierde.
 * 2. **El carrito se opera con teclado.** Flechas para moverse, `+`/`-` para
 *    cantidades, Supr para quitar, F4 para cobrar.
 * 3. **Cuando no hay red, se sigue vendiendo.** La venta se encola con su clave
 *    de idempotencia y se sincroniza al volver, sin cobrar dos veces.
 */
export function PosScreen() {
  const session = getSession();
  const branch = session?.branches[0] ?? null;
  const online = useOnlineStatus();
  const outbox = useOutbox();
  const catalog = useCatalogSync();

  // `useReducer` y no `useState`: las reglas del carrito viven en el reductor,
  // que se prueba aparte y no depende de la pantalla.
  const [state, dispatch] = useReducer(cartReducer, emptyCart);
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<SearchHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [message, setMessage] = useState<{ tone: "ok" | "error"; text: string } | null>(null);
  const [paying, setPaying] = useState(false);
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [pricesIncludeTax, setPricesIncludeTax] = useState(true);
  const searchRef = useRef<HTMLInputElement>(null);

  const totals = useMemo(
    () =>
      cartTotals(
        state.lines.map((line) =>
          lineTotals(
            line.unitPriceCents,
            line.quantity,
            line.discountCents,
            line.tax,
            pricesIncludeTax,
          ),
        ),
        state.globalDiscountCents,
      ),
    [state, pricesIncludeTax],
  );

  // El buscador recupera el foco cuando no hay ningún diálogo abierto.
  useEffect(() => {
    if (!paying && !receipt) searchRef.current?.focus();
  }, [paying, receipt]);

  // Búsqueda con retardo: el servidor primero, la caché local si no hay red.
  useEffect(() => {
    const query = term.trim();
    if (query.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    // `void (async () => …)()` y no `setTimeout(async () => …)`: pasar una
    // función que devuelve promesa a `setTimeout` describe una intención que no
    // es la real —nadie espera ese resultado— y el linter tiene razón en
    // señalarlo.
    const timer = setTimeout(() => {
      void (async () => {
      try {
        if (online && session) {
          const found = await searchProducts(query, branch?.id);
          if (!cancelled) setResults(found.items);
        } else {
          const cached: CachedProduct[] = await searchCatalog(query);
          if (!cancelled) setResults(cached.map(cachedToHit));
        }
      } catch (error) {
        if (!cancelled) {
          setMessage({
            tone: "error",
            text: error instanceof ApiError ? error.message : "No se pudo buscar",
          });
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
      })();
    }, 180);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [term, online, session, branch?.id]);

  /**
   * Agrega un producto al ticket.
   *
   * El precio y el impuesto los resuelve **el servidor** (es lo que garantiza
   * que el total del ticket sea el mismo que va a cobrar). Si no hay red, se usa
   * lo que quedó en la caché del catálogo, con la tasa de IVA por defecto.
   */
  async function addHit(hit: SearchHit) {
    if (hit.price_cents == null) {
      setMessage({ tone: "error", text: `"${hit.product_name}" no tiene precio asignado` });
      return;
    }

    let unitPriceCents = hit.price_cents;
    let tax: CartLine["tax"] = null;
    let priceSource = "Caché local";

    if (online) {
      try {
        const resolved = await resolvePrice(hit.variant_id, { quantity: "1", branchId: branch?.id });
        unitPriceCents = resolved.unit_price_cents;
        priceSource = resolved.price_list_code ?? "Servidor";
        setPricesIncludeTax(resolved.prices_include_tax);
        if (resolved.tax_rate && Number.parseFloat(resolved.tax_rate) > 0) {
          tax = {
            code: resolved.tax_code ?? "IVA",
            rate: Number.parseFloat(resolved.tax_rate),
            satCode: resolved.sat_code ?? "",
            satFactor: "",
          };
        }
      } catch {
        priceSource = "Caché local";
      }
    }

    dispatch({
      type: "add",
      line: {
        variantId: hit.variant_id,
        sku: hit.sku,
        name: hit.product_name,
        uomCode: null,
        unitPriceCents,
        tax,
        tracked: hit.tracked ?? false,
        priceSource,
      },
    });
    setTerm("");
    setResults([]);
    setMessage(null);
  }

  async function charge(payments: { method: string; amount_cents: number; tip_cents: number }[]) {
    if (!session || !branch) return;

    const payload = {
      branch_id: branch.id,
      series: "A",
      lines: state.lines.map((line) => ({
        variant_id: line.variantId,
        quantity: line.quantity.toFixed(4),
        // El descuento va en CENTAVOS, calculados aquí a partir del porcentaje
        // que aplicó el cajero. El servidor vuelve a validar el rango.
        discount_cents: line.discountCents,
      })),
      payments: payments.map((payment) => ({
        method: payment.method as "efectivo",
        amount_cents: payment.amount_cents,
        tip_cents: payment.tip_cents,
      })),
      discount_cents: state.globalDiscountCents,
    };

    // La clave se genera UNA vez por venta: si se generara en cada reintento,
    // cada intento sería una venta distinta (ADR-0016).
    const key = newIdempotencyKey();

    try {
      const { sale, queued } = await submitSale(payload, key);
      if (sale) {
        setReceipt({ folio: `${sale.series}-${sale.folio}`, sale });
      } else if (queued) {
        setReceipt({ folio: `local-${queued.localNumber}`, sale: null });
      }
      dispatch({ type: "clear" });
      setPaying(false);
    } catch (error) {
      setMessage({
        tone: "error",
        text: error instanceof ApiError ? error.message : "No se pudo cobrar",
      });
    }
  }

  useShortcuts(
    {
      [POS_SHORTCUTS.search]: () => searchRef.current?.focus(),
      [POS_SHORTCUTS.charge]: () => {
        if (canCharge(state)) setPaying(true);
      },
      [POS_SHORTCUTS.clearCart]: () => {
        dispatch({ type: "clear" });
        setMessage({ tone: "ok", text: "Ticket vaciado" });
      },
      [POS_SHORTCUTS.removeLine]: () => dispatch({ type: "remove", index: state.selectedIndex }),
      [POS_SHORTCUTS.moveDown]: () => dispatch({ type: "move", delta: 1 }),
      [POS_SHORTCUTS.moveUp]: () => dispatch({ type: "move", delta: -1 }),
      [POS_SHORTCUTS.increase]: () => dispatch({ type: "increment", index: state.selectedIndex }),
      [POS_SHORTCUTS.decrease]: () => dispatch({ type: "decrement", index: state.selectedIndex }),
      // Alias cómodos cuando el foco no está en el buscador.
      "+": () => dispatch({ type: "increment", index: state.selectedIndex }),
      "-": () => dispatch({ type: "decrement", index: state.selectedIndex }),
      [POS_SHORTCUTS.discount]: () => {
        dispatch({ type: "lineDiscount", index: state.selectedIndex, percent: 10 });
        setMessage({ tone: "ok", text: "10 % de descuento en la línea seleccionada" });
      },
      Enter: () => {
        if (term.trim() && results[0]) void addHit(results[0]);
      },
    },
    !paying && !receipt,
  );

  const syncedLabel = catalog.syncedAt
    ? new Date(catalog.syncedAt).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <div data-pos-surface className="grid h-[calc(100vh-4rem)] grid-cols-1 xl:grid-cols-[1fr_30rem]">
      {/* ------------------------- Catálogo ------------------------- */}
      <section className="flex min-w-0 flex-col gap-4 p-6">
        <div className="flex flex-wrap items-center gap-3">
          <label className="relative min-w-64 flex-1">
            <span className="sr-only">Buscar producto o escanear código de barras</span>
            <input
              ref={searchRef}
              type="search"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder="Escanea un código o escribe para buscar…"
              className="focus:border-electric h-14 w-full rounded-msr border border-line bg-snow px-4 text-lg text-carbon placeholder:text-muted focus:outline-none"
            />
          </label>
          <div className="text-right text-xs leading-5 text-muted">
            <p>
              <kbd className="kbd">F2</kbd> buscar · <kbd className="kbd">F4</kbd> cobrar ·{" "}
              <kbd className="kbd">F8</kbd> descuento · <kbd className="kbd">F9</kbd> vaciar
            </p>
            <p>
              <kbd className="kbd">↑↓</kbd> moverse · <kbd className="kbd">F5</kbd>–
              <kbd className="kbd">F6</kbd> cantidad · <kbd className="kbd">Supr</kbd> quitar
            </p>
          </div>
        </div>

        {message ? (
          <p
            role="status"
            className={
              message.tone === "ok"
                ? "border-success/30 bg-success/10 text-success rounded-msr border px-3 py-2 text-sm"
                : "border-danger/30 bg-danger/10 text-danger rounded-msr border px-3 py-2 text-sm"
            }
          >
            {message.text}
          </p>
        ) : null}

        <div className="min-h-0 flex-1 overflow-auto">
          {results.length > 0 ? (
            <ul className="grid grid-cols-1 gap-2 lg:grid-cols-2">
              {results.map((hit, index) => (
                <li key={hit.variant_id}>
                  <button
                    type="button"
                    onClick={() => void addHit(hit)}
                    className={`rounded-msr flex w-full items-center justify-between gap-3 border px-4 py-3 text-left transition-colors ${
                      index === 0
                        ? "border-electric bg-electric/10 text-carbon"
                        : "border-line bg-snow text-carbon hover:bg-surface"
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-carbon">{hit.product_name}</span>
                      <span className="font-mono text-xs text-muted">{hit.sku}</span>
                    </span>
                    <Money cents={hit.price_cents ?? 0} className="font-semibold text-carbon" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="grid h-full place-items-center text-center text-sm text-muted">
              <div className="space-y-2">
                {searching ? (
                  <p>Buscando…</p>
                ) : (
                  <>
                    <p className="text-2xl">🛒</p>
                    <p>
                      Escanea un código de barras o escribe al menos dos letras.
                      <br />
                      {catalog.products > 0
                        ? `${catalog.products} productos en la caché local${syncedLabel ? ` · sincronizada a las ${syncedLabel}` : ""}.`
                        : "Sincronizando el catálogo…"}
                    </p>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* --------------------------- Ticket -------------------------- */}
      <aside className="border-line bg-ink text-white flex min-h-0 flex-col border-l">
        <header className="flex items-center justify-between border-b border-white/10 px-5 py-3">
          <div>
            <h2 className="font-display text-base font-bold text-white">Ticket en curso</h2>
            <p className="text-xs text-white/60">
              {online ? "En línea" : "Sin conexión · se enviará al volver"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {outbox.pending > 0 ? (
              <StatusPill tone="warning">{outbox.pending} por enviar</StatusPill>
            ) : null}
            {outbox.rejected > 0 ? (
              <StatusPill tone="danger">
                <TriangleAlert aria-hidden className="size-3" /> {outbox.rejected}
              </StatusPill>
            ) : null}
          </div>
        </header>

        <div className="min-h-0 flex-1 space-y-1 overflow-auto px-3 py-3">
          {state.lines.length === 0 ? (
            <p className="px-3 py-10 text-center text-sm text-white/50">El ticket está vacío.</p>
          ) : (
            state.lines.map((line, index) => (
              <div
                key={line.variantId}
                onMouseEnter={() => dispatch({ type: "select", index })}
                className={`rounded-msr flex items-center gap-2 border px-3 py-2 ${
                  index === state.selectedIndex ? "border-electric bg-electric/15" : "border-white/10 bg-white/5"
                }`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-white">{line.name}</p>
                  <p className="text-xs text-white/60">
                    <Money cents={line.unitPriceCents} size="sm" /> c/u
                    {line.discountCents > 0 ? (
                      <span className="text-warning">
                        {" "}
                        · −<Money cents={line.discountCents} size="sm" />
                      </span>
                    ) : null}
                  </p>
                </div>

                <div className="flex items-center gap-1">
                  <IconAction
                    label={`Quitar una unidad de ${line.name}`}
                    onClick={() => dispatch({ type: "decrement", index })}
                  >
                    <Minus aria-hidden className="size-3.5" />
                  </IconAction>
                  <span className="tabular w-10 text-center text-sm font-semibold text-white">
                    {line.quantity}
                  </span>
                  <IconAction
                    label={`Agregar una unidad de ${line.name}`}
                    onClick={() => dispatch({ type: "increment", index })}
                  >
                    <Plus aria-hidden className="size-3.5" />
                  </IconAction>
                </div>

                <Money
                  cents={line.unitPriceCents * line.quantity - line.discountCents}
                  size="sm"
                  className="w-20 text-right font-semibold text-white"
                />

                <IconAction
                  label={`Eliminar ${line.name}`}
                  onClick={() => dispatch({ type: "remove", index })}
                >
                  <Trash2 aria-hidden className="size-3.5" />
                </IconAction>
              </div>
            ))
          )}
        </div>

        <footer className="space-y-3 border-t border-white/10 px-5 py-4">
          <dl className="space-y-1 text-sm text-white/70">
            <Row label="Subtotal">
              <Money cents={totals.subtotalCents} size="sm" />
            </Row>
            <Row label="IVA">
              <Money cents={totals.taxCents} size="sm" />
            </Row>
            {totals.discountCents > 0 ? (
              <Row label={`Descuento (máx ${MAX_DISCOUNT * 100} %)`}>
                <Money cents={-totals.discountCents} size="sm" className="text-warning" />
              </Row>
            ) : null}
          </dl>

          <div className="flex items-end justify-between border-t border-white/10 pt-3">
            <span className="text-sm font-semibold text-white">Total</span>
            <Money cents={totals.totalCents} size="lg" className="font-bold text-white" />
          </div>

          <Button
            size="lg"
            fullWidth
            disabled={!canCharge(state)}
            onClick={() => setPaying(true)}
            icon={<span className="text-xs opacity-70">F4</span>}
          >
            Cobrar
          </Button>
        </footer>
      </aside>

      {paying ? (
        <PaymentDialog
          totalCents={totals.totalCents}
          onCancel={() => setPaying(false)}
          onConfirm={(payments) => void charge(payments)}
        />
      ) : null}

      {receipt ? (
        <ReceiptDialog
          folio={receipt.folio}
          sale={receipt.sale}
          onClose={() => setReceipt(null)}
        />
      ) : null}
    </div>
  );
}

/** Convierte un producto de la caché local al mismo formato que devuelve el buscador. */
function cachedToHit(item: CachedProduct): SearchHit {
  return {
    variant_id: item.variantId,
    product_id: item.productId,
    sku: item.sku,
    name: item.name,
    product_name: item.productName,
    kind: item.kind,
    barcode: item.barcode,
    price_cents: item.priceCents,
    tracked: item.tracked,
    matched_by: "nombre",
  };
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between">
      <dt>{label}</dt>
      <dd className="text-white/80">{children}</dd>
    </div>
  );
}

function IconAction({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="grid size-8 place-items-center rounded-md border border-white/10 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
    >
      {children}
    </button>
  );
}
